import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, eur, beDate } from "@/lib/studioApi";
import { ListSkeleton } from "@/components/studio/StudioUI";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const thisYear = new Date().getFullYear();

export default function StudioRevenue() {
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    studio.get("/whoami")
      .then(({ data }) => data.is_owner && studio.get(`/revenue?year=${year}`).then(({ data }) => setData(data)))
      .catch(() => {});
  }, [year]);

  const maxMonth = Math.max(1, ...((data?.months || []).map((m) => m.net)));

  return (
    <StudioLayout
      ownerOnly
      title="Chiffre d'affaires"
      subtitle="Vos devis acceptés et signés, depuis le 1er janvier, par trimestre et par mois."
      action={
        <select data-testid="revenue-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-xl border border-[#E2E7F7] bg-white px-4 py-2 text-sm font-semibold text-[#0F2645]">
          {[thisYear, thisYear - 1, thisYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      }
    >
      {!data && <ListSkeleton />}

      {data && (
        <div className="mt-6 space-y-6">
          <div data-testid="revenue-summary" className="dv-navy rounded-2xl border border-white/10 p-6 text-white shadow-[0_18px_44px_-26px_rgba(15,38,69,0.55)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-200">Depuis le 1er janvier {data.year}</p>
            <p data-testid="revenue-net" className="mt-2 font-heading text-3xl sm:text-4xl font-bold">{eur(data.net)}</p>
            <p className="mt-1 text-sm text-blue-100">HTVA · {eur(data.total)} TVAC · {data.count} devis signés</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.quarters.map((q) => (
              <div
                key={q.quarter}
                data-testid={`revenue-quarter-${q.quarter}`}
                className={`rounded-2xl border p-4 ${q.quarter === data.current_quarter ? "border-[#3A5CC8] bg-[#EEF1FC]" : "border-[#E2E7F7] bg-white"}`}
              >
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Trimestre {q.quarter}</p>
                <p className="mt-1 font-heading text-lg font-bold text-[#0F2645]">{eur(q.net)}</p>
                <p className="text-xs text-[#5C6A87]">{q.count} devis</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <p className="font-heading text-base font-semibold text-[#0F2645]">Par mois (HTVA)</p>
            <div className="mt-4 space-y-2">
              {data.months.map((m, i) => (
                <div key={m.month} data-testid={`revenue-month-${m.month}`} className="flex items-center gap-3">
                  <span className="w-20 shrink-0 text-xs font-semibold text-[#5C6A87]">{MONTHS[i]}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-[#3A5CC8] transition-all duration-500" style={{ width: `${(m.net / maxMonth) * 100}%` }} />
                  </div>
                  <span className="w-24 shrink-0 text-right text-xs font-bold text-[#0F2645]">{eur(m.net)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <p className="font-heading text-base font-semibold text-[#0F2645]">Devis signés</p>
            <div data-testid="revenue-quotes" className="mt-3 space-y-2">
              {data.quotes.map((q) => (
                <Link key={q.id} to={`/studio/devis/${q.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-4 py-3 hover:border-[#3A5CC8] transition-colors duration-200">
                  <span className="text-sm font-semibold text-[#0F2645]">{q.number} · {q.client_name}</span>
                  <span className="text-xs text-[#5C6A87]">{beDate(q.date)}</span>
                  <span className="text-sm font-bold text-[#0F2645]">{eur(q.net)}</span>
                </Link>
              ))}
              {!data.quotes.length && (
                <p className="flex items-center gap-2 text-sm text-[#5C6A87]">
                  <TrendingUp className="w-4 h-4 text-slate-300" /> Aucun devis signé sur {data.year}.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
